import { useState } from 'react';
import { api } from '../lib/api';

const SCOPE_OPTIONS = [
  { id: 'profile', label: 'Profile & KB' },
  { id: 'cvs', label: 'CVs' },
  { id: 'cover_letters', label: 'Cover Letters' },
  { id: 'tracker', label: 'Application Tracker' },
  { id: 'analytics', label: 'Analytics' },
];

export default function MentorsPage() {
  const [email, setEmail] = useState('');
  const [scope, setScope] = useState<string[]>(['profile', 'cvs', 'cover_letters']);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function handleInvite() {
    if (!email.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await api.post<{ message: string }>('/api/mentors/invite', {
        email: email.trim(),
        scope,
      });
      setResult(data.message);
      setEmail('');
    } catch (err) {
      setResult(`Error: ${(err as Error).message}`);
    }
    setLoading(false);
  }

  function toggleScope(id: string) {
    setScope((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Mentors</h1>
      <p className="text-gray-600 mb-6">
        Invite a mentor (parent, tutor, careers adviser) to review your work and leave suggestions.
      </p>

      {/* Invite form */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <h2 className="font-medium text-gray-900 mb-3">Invite a Mentor</h2>

        <label className="block text-sm text-gray-600 mb-1" htmlFor="mentor-email">
          Mentor's email address
        </label>
        <input
          id="mentor-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="parent@example.com"
          className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
        />

        <fieldset className="mb-4">
          <legend className="text-sm text-gray-600 mb-2">What can they see?</legend>
          <div className="flex flex-wrap gap-2">
            {SCOPE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggleScope(opt.id)}
                className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                  scope.includes(opt.id)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
                aria-pressed={scope.includes(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>

        <button
          onClick={handleInvite}
          disabled={loading || !email.trim() || scope.length === 0}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Inviting...' : 'Send Invite'}
        </button>

        {result && (
          <p className={`mt-3 text-sm ${result.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
            {result}
          </p>
        )}
      </div>

      <div className="text-sm text-gray-500">
        <p>If the mentor doesn't have an account, one will be created and they'll receive a signup email with a temporary password.</p>
        <p className="mt-1">Mentors can read shared items and leave suggestions — they cannot edit your content.</p>
      </div>
    </div>
  );
}
